import { SyntheticEvent } from "react";
import { Navigate, Outlet, RouteObject, useNavigate, useParams } from "react-router-dom";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import { Box, Container, styled, Tab, TabProps, tabsClasses, Tooltip } from "@mui/material";
import useDatasetTableFQNs from "datasets/hooks/useDatasetTableFQNs";
import Configurator from "datasets/pages/settings/pages/Configurator";
import GeneralSettings from "datasets/pages/settings/pages/GeneralSettings";
import UserAccess from "datasets/pages/settings/pages/UserAccess";
import yn from "yn";
import { ProtectedAppRoute } from "~/components/ProtectedAppRoute";
import { useSafeRoutes } from "~/hooks/useSafeRoutes";
import useUserPermission from "~/hooks/useUserPermission";
import { trackEvent } from "~/utils/monitoring";

const { VITE_COHORT_EXPLORER_ENABLED, VITE_DATASET_ACCESS_ENABLED } = import.meta.env;

const StyledTabList = styled(TabList)({
  borderBottom: "none",
  minWidth: 131,
  position: "sticky",
  height: "fit-content",
  top: 82.5,
  [`& .${tabsClasses.list}`]: {
    flexDirection: "column",
    gap: 8,
  },
  [`& .${tabsClasses.indicator}`]: {
    display: "none",
  },
});

const StyledTab = styled(Tab)(({ theme }) => ({
  alignItems: "start",
  padding: "6px 12px",
  borderRadius: "4px",
  "&:hover": {
    backgroundColor: theme.palette.grey[200],
  },
  "&[aria-selected=true]": {
    fontWeight: 600,
    color: theme.palette.text.primary,
    backgroundColor: theme.palette.grey[200],
  },
}));

const routeDefinitions: RouteObject[] = [
  { index: true, element: <Navigate to="general" replace /> },
  { path: "general", element: <GeneralSettings /> },
  { path: "access", element: <UserAccess /> },
  {
    path: "cohort/*",
    element: (
      <ProtectedAppRoute permission="view:cohort-explorer-config-app">
        <Configurator />
      </ProtectedAppRoute>
    ),
  },
];

interface TabWithTooltipProps extends TabProps {
  disabledReason?: string;
}

const TabWithTooltip = ({
  label,
  disabled = false,
  disabledReason = "",
  ...otherProps
}: TabWithTooltipProps) => {
  // Wrap in a <span> so Tooltip can attach to an element even when the child is disabled
  if (disabled && disabledReason) {
    return (
      <Tooltip arrow title={disabledReason}>
        <span>
          <StyledTab label={label} disabled {...otherProps} />
        </span>
      </Tooltip>
    );
  }

  return <StyledTab label={label} disabled={disabled} {...otherProps} />;
};

const DatasetSettings = () => {
  const navigate = useNavigate();
  const { "*": subRoute } = useParams();
  const routes = useSafeRoutes(routeDefinitions);
  const { data: tableFQNs, isLoading: loadingTableFQNs } = useDatasetTableFQNs();
  const hasConfiguratorPermission = useUserPermission("view:cohort-explorer-config-app");

  const configuratorEnabled = yn(VITE_COHORT_EXPLORER_ENABLED) && hasConfiguratorPermission;
  const userAccessEnabled = yn(VITE_DATASET_ACCESS_ENABLED);
  const currentTab = subRoute?.split("/")[0] || "general";
  const hasConfiguredTables = !!tableFQNs?.length;
  const showTabList = configuratorEnabled || userAccessEnabled;

  const handleTabChange = (_: SyntheticEvent, selectedTab: string) => {
    navigate(selectedTab);
    trackEvent("dataset_settings_tab_changed", {
      tab: selectedTab,
      previous_tab: currentTab,
    });
  };

  return (
    <TabPanel value="settings">
      <Container sx={{ display: "flex", gap: 4, position: "relative" }}>
        <TabContext value={currentTab}>
          {showTabList && (
            <StyledTabList onChange={handleTabChange}>
              <StyledTab label="General" value="general" />
              {userAccessEnabled && <StyledTab label="User Access" value="access" />}
              {configuratorEnabled && (
                <TabWithTooltip
                  label="Cohort Explorer"
                  value="cohort"
                  disabled={!hasConfiguredTables}
                  disabledReason={
                    loadingTableFQNs || hasConfiguredTables
                      ? ""
                      : "Add tables to dataset to enable configuration"
                  }
                />
              )}
            </StyledTabList>
          )}
          <Box flex={1} maxWidth={showTabList ? "calc(100% - 32px - 131px)" : "100%"}>
            {routes}
            <Outlet />
          </Box>
        </TabContext>
      </Container>
    </TabPanel>
  );
};

export default DatasetSettings;
